<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-box-seam me-2"></i>Products Management
      </h4>
      <div v-if="profile.role !== 'user'" class="d-flex">
        <button class="btn btn-primary" @click="addProduct">
          <i class="bi bi-plus-circle me-1"></i> Add Product
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading products...</p>
      </div>

      <!-- Products Content -->
      <div v-if="!loading" class="products-content fade-in">
        <!-- Products Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ paginationData.total || 0 }}</span>
            <span class="text-muted">Products</span>
          </div>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <select v-model="statusFilter" class="form-select w-auto" @change="onStatusFilterChange" data="">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="discontinued">Discontinued</option>
              <option value="inactive">Inactive</option>
            </select>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search product" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Products Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th></th>
                <th>SKU</th>
                <th>CODE</th>
                <th>NAME</th>
                <th class="text-end">PRICE</th>
                <th>STOCK</th>
                <th>R. STATUS</th>
                <th>STATUS</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(product, index) in dataTable" :key="product.id" class="product-row" :class="{'bg-light': index % 2 === 0}">
                <td>
                  <img
                      :src="product.avatar ?? 'https://chrisdellasega.com/wp-content/uploads/2023/03/image-placeholder.jpg'"
                      width="30"
                      class="product-avatar rounded shadow-sm"
                      alt="Product Image"
                  />
                </td>
                <td>{{ product.sku }}</td>
                <td>{{ product.code }}</td>
                <td>{{ product.name }}</td>
                <td class="text-end">{{ formatNumber(product.sell) }}</td>
                <td>{{ product.stock }} {{ product.unit.name }}</td>
                <td>
                  <Badge pill :type="getPillByReorder(product.reorder < product.stock ? 'good' : 'reorder')">
                    {{ product.reorder < product.stock ? 'good' : 'reorder' }}
                  </Badge>
                </td>
                <td>
                  <Badge pill :type="getPillByStatus(product.status)">
                    {{ product.status }}
                  </Badge>
                </td>
                <td class="text-end">
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-primary" @click="productDetails(product.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                    <button
                      v-if="profile.role !== 'user' && product.status !== 'discontinued'"
                      class="btn btn-outline-warning"
                      @click="toggleDiscontinue(product, 'discontinued')"
                      title="Discontinue this product"
                    >
                      <i class="bi bi-pause-circle"></i> Discontinue
                    </button>
                    <button
                      v-if="profile.role !== 'user' && product.status === 'discontinued'"
                      class="btn btn-outline-success"
                      @click="toggleDiscontinue(product, 'active')"
                      title="Reactivate this product"
                    >
                      <i class="bi bi-play-circle"></i> Reactivate
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,15,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              ref="pagination"
              v-model="dataTablePage"
              :pages="paginationData.last_page || 1"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import productService from "@/api/productService";
import formatNumber from "@/utils/formatNumber";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import Pagination from "@/components/pagination/Pagination.vue";

export default {
  name: "Products",

  components: {
    Pagination
  },

  data(){
    return {
      profile: {},
      loading: true,
      statusFilter: this.$route?.query?.status ?? '',
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 15,
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      subscription: {
        package: {}
      },
      dataTable: [],
      paginationData: {},
    }
  },

  methods:{
    formatNumber,
    getPillByReorder(cellData) {
      switch (cellData) {
        case 'reorder':
          return 'danger';
        case 'good':
          return 'success';
      }
    },
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'secondary';
        case 'active':
          return 'success';
        case 'discontinued':
          return 'warning';
        default:
          return 'info';
      }
    },
    async toggleDiscontinue(product, newStatus) {
      const actionText = newStatus === 'discontinued' ? 'discontinue' : 'reactivate';
      const confirmed = window.confirm(`Are you sure you want to ${actionText} "${product.name}"?`);
      if (!confirmed) return;

      try {
        await productService.updateProductStatus(product.id, newStatus);
        product.status = newStatus;
      } catch (error) {
        console.error(`Error updating product status:`, error);
      }
    },
    onStatusFilterChange() {
      this.dataTablePage = 1;
      this.getProducts();
    },
    async getProducts() {
      this.loading = true;
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
        status: this.statusFilter || undefined,
      };
      
      try {
        const response = await productService.getProducts(params);
        
        if (response && response.data) {
          this.dataTable = response.data;
          this.paginationData = {
            current_page: response.current_page,
            last_page: response.last_page,
            per_page: response.per_page,
            total: response.total,
          };
        } else {
          this.dataTable = response || [];
          this.paginationData = {};
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        this.loading = false;
      }
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getProducts()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getProducts()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 15 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    productDetails(id) {
      router.push('/product/' + id)
    },
    addProduct(){
      router.push('/add-product')
    },
  },

  computed: {},


  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getProducts();
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getProducts();
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getProducts()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
/* Products page styles */
.products-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.product-row {
  transition: all 0.2s ease;
}

.product-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
